import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo, useState } from "react";
import { Calendar, dayjsLocalizer } from "react-big-calendar";
import { ReactComponent as DateRangeIcon } from "../../assets/img/events/date.svg";
import { ReactComponent as ChevronDoubleLeftIcon } from "assets/img/icons/sidebar/chevron-double-left.svg";
import { ReactComponent as ChevronDoubleRightIcon } from "assets/img/icons/sidebar/chevron-double-right.svg";
import { Button } from "components/elements";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { useUserTimezone } from "hooks/useUserTimezone";
import { trpc } from "lib/trpc";
import "react-big-calendar/lib/css/react-big-calendar.css";
import type { CalendarFriendlyCompassEvent } from "./types";
import { CompassEventDetailsModal } from "./CompassEventDetailsModal";
import { CompassEventFormModal } from "./CompassEventFormModal";

dayjs.extend(utc);
dayjs.extend(timezone);

const localizer = dayjsLocalizer(dayjs);

export const CompassEvents = () => {
    const timeZone = useUserTimezone();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedEvent, setSelectedEvent] = useState<CalendarFriendlyCompassEvent | null>(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState<CalendarFriendlyCompassEvent | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [isAsideCollapsed, setIsAsideCollapsed] = useState(false);

    const monthStart = useMemo(() => dayjs(currentDate).startOf("month").toDate(), [currentDate]);
    const monthEnd = useMemo(() => dayjs(currentDate).endOf("month").toDate(), [currentDate]);

    const { data: eventsInRange = [] } = trpc.compassEvents.latest.useQuery({
        from: monthStart,
        to: monthEnd,
    });
    const { data: futureEvents = [] } = trpc.compassEvents.allFutureEvents.useQuery();
    const queryClient = trpc.useContext();

    const eventsFormatted = useMemo(
        () =>
            eventsInRange.map((item) => {
                const startDate = new Date(item.start);
                const endDate = item.end ? new Date(item.end) : new Date(item.start);
                const validEndDate = endDate < startDate ? startDate : endDate;
                return {
                    ...item,
                    start: startDate,
                    end: validEndDate,
                    createdAt: new Date(item.createdAt),
                    updatedAt: new Date(item.updatedAt),
                };
            }),
        [eventsInRange],
    );

    const upcomingEvents = useMemo(() => {
        return futureEvents
            .map((item) => ({
                ...item,
                start: new Date(item.start),
                end: item.end ? new Date(item.end) : null,
                createdAt: new Date(item.createdAt),
                updatedAt: new Date(item.updatedAt),
            }))
            .slice(0, 10);
    }, [futureEvents]);

    const onSelectEvent = useCallback((event: CalendarFriendlyCompassEvent) => {
        setSelectedEvent(event);
        setShowDetailsModal(true);
    }, []);

    const onNavigate = useCallback((newDate: Date) => {
        setCurrentDate(newDate);
    }, []);

    const handleCreateEvent = () => {
        setEditingEvent(null);
        setShowFormModal(true);
    };

    const handleEditEvent = (event: CalendarFriendlyCompassEvent) => {
        setEditingEvent(event);
        setShowDetailsModal(false);
        setShowFormModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedEvent(null);
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
        setEditingEvent(null);
    };

    const handleExportExcel = async () => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const base64Data = await queryClient.compassEvents.exportExcel.fetch();
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const fileName = `calendar_events_${new Date().toISOString().split("T")[0]}.xlsx`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            enqueueSnackbar({ message: "Excel file exported successfully", variant: "success" });
        } catch (error) {
            console.error("Error exporting Excel:", error);
            enqueueSnackbar({ message: "Error exporting Excel file", variant: "error" });
        } finally {
            setIsExporting(false);
        }
    };

    const eventStyleGetter = () => ({
        style: {
            backgroundColor: "#2DA7E0",
            borderRadius: "4px",
            opacity: 0.8,
            color: "white",
            border: "0px",
            display: "block",
        },
    });

    return (
        <>
            {selectedEvent && (
                <CompassEventDetailsModal
                    isOpen={showDetailsModal}
                    onClose={handleCloseDetailsModal}
                    event={selectedEvent}
                    onEdit={handleEditEvent}
                />
            )}
            <CompassEventFormModal
                isOpen={showFormModal}
                onClose={handleCloseFormModal}
                editEvent={editingEvent}
            />

            <div className="mt-6 font-sans text-[28px] font-semibold leading-normal text-[#424242]">
                My Calendar
            </div>

            <div className="mx-auto flex w-full gap-6 py-6">
                {/* Main Content — Calendar */}
                <main className="flex flex-grow flex-col gap-6">
                    <div className="rounded-2xl border border-[#c8c8c8] bg-white p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h2 className="font-sans text-[20px] font-semibold leading-[100%] text-[#424242]">
                                {dayjs(currentDate).tz(timeZone).format("MMMM YYYY")}
                            </h2>
                            <div className="flex gap-3">
                                <Button
                                    onClick={handleExportExcel}
                                    variant="secondary"
                                    size="smaller"
                                    disabled={isExporting}
                                >
                                    {isExporting ? "Exporting..." : "Export Excel"}
                                </Button>
                                <Button
                                    onClick={handleCreateEvent}
                                    color="blue"
                                    variant="primary"
                                    size="smaller"
                                >
                                    + New Event
                                </Button>
                            </div>
                        </div>
                        <div style={{ height: "600px" }}>
                            <Calendar
                                localizer={localizer}
                                events={eventsFormatted}
                                startAccessor="start"
                                endAccessor="end"
                                onSelectEvent={onSelectEvent}
                                onNavigate={onNavigate}
                                date={currentDate}
                                view="month"
                                views={["month"]}
                                eventPropGetter={eventStyleGetter}
                                components={{
                                    event: ({ title }) => (
                                        <span className="line-clamp-3 block whitespace-normal text-[11px] leading-tight">
                                            {title}
                                        </span>
                                    ),
                                }}
                                toolbar={false}
                            />
                        </div>
                        <div className="mt-4 flex justify-between">
                            <Button
                                onClick={() =>
                                    onNavigate(dayjs(currentDate).subtract(1, "month").toDate())
                                }
                                variant="outline"
                                size="smaller"
                            >
                                ← Previous
                            </Button>
                            <Button
                                onClick={() => onNavigate(new Date())}
                                variant="outline"
                                size="smaller"
                            >
                                Today
                            </Button>
                            <Button
                                onClick={() =>
                                    onNavigate(dayjs(currentDate).add(1, "month").toDate())
                                }
                                variant="outline"
                                size="smaller"
                            >
                                Next →
                            </Button>
                        </div>
                    </div>
                </main>

                {/* Right Aside - Upcoming Events */}
                <aside
                    className={`relative flex flex-col transition-all duration-300 ${
                        isAsideCollapsed ? "w-10" : "w-[30%]"
                    }`}
                >
                    {/* Toggle button — always visible */}
                    <button
                        onClick={() => setIsAsideCollapsed(!isAsideCollapsed)}
                        className="absolute -left-3 top-4 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full border border-[#c8c8c8] bg-white shadow-sm hover:bg-gray-50"
                    >
                        {isAsideCollapsed ? (
                            <ChevronDoubleLeftIcon className="h-3.5 w-3.5" />
                        ) : (
                            <ChevronDoubleRightIcon className="h-3.5 w-3.5" />
                        )}
                    </button>

                    {/* Collapsed state: rotated label */}
                    {isAsideCollapsed ? (
                        <div className="flex h-[750px] items-center justify-center rounded-2xl border border-[#c8c8c8] bg-[#fafffe]">
                            <span
                                className="select-none whitespace-nowrap font-sans text-[14px] font-semibold text-[#424242]"
                                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                            >
                                Upcoming Events
                            </span>
                        </div>
                    ) : (
                        <div className="mx-auto flex h-[750px] w-full flex-col rounded-2xl border border-[#c8c8c8] bg-[#fafffe] p-4">
                            <h2 className="mb-4 font-sans text-[18px] font-semibold text-[#424242]">
                                Upcoming Events
                            </h2>
                            <div className="flex-1 space-y-3 overflow-y-auto pr-2">
                                {upcomingEvents.length === 0 ? (
                                    <p className="text-sm text-gray-500">No upcoming events</p>
                                ) : (
                                    upcomingEvents.map((event) => (
                                        <div
                                            key={event.id}
                                            onClick={() => onSelectEvent(event)}
                                            className="cursor-pointer rounded-lg border border-[#c8c8c8] bg-white p-3 transition-shadow hover:shadow-md"
                                        >
                                            <div className="mb-2 flex items-center justify-between">
                                                <h3 className="line-clamp-2 font-sans text-[16px] font-semibold text-[#424242]">
                                                    {event.title}
                                                </h3>
                                                <FavoriteButton
                                                    target={{ type: "compassEvent", id: event.id }}
                                                    size={16}
                                                />
                                            </div>
                                            <div className="flex items-center gap-2 text-sm text-[#424242]/90">
                                                <DateRangeIcon className="h-4 w-4" />
                                                <span>
                                                    {dayjs(event.start)
                                                        .tz(timeZone)
                                                        .format("MMM D, YYYY")}
                                                </span>
                                            </div>
                                            {event.description && (
                                                <p className="line-clamp-2 mt-2 text-sm text-[#424242]/80">
                                                    {event.description}
                                                </p>
                                            )}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </aside>
            </div>
        </>
    );
};
